<script>
	import { onDestroy } from "svelte";
	import { page } from "$app/stores";
	import { watchObject, tryEval } from "cando-util";

	import { store } from "$lib/utilities/store";
	import {
		resolveTableFields,
		buildAutoTableFields,
	} from "./tableColumnsUtil.js";
	import {
		action,
		clearRecordSelection,
		updateSearchParams,
	} from "./tableFunctions.js";
	import { setResponsesDb } from "../../FormSubmission/getResponsesDb.js";
	import { recordUpdate } from "./recordUpdate.js";
	import { formUpdate } from "$lib/sections/Admin/formUpdate";
	import {
		createSetting,
		scheduleMissingSettingUpdate,
	} from "./dashboardSettings.js";

	import DashboardTable from "./DashboardTable.svelte";
	import RecordDetail from "./RecordDetail.svelte";
	import Form from "../../Form.svelte";
	import SettingsFormInput from "../SettingsFormInput.svelte";

	export let col;
	export let settingSuffix;
	export let dbGroup;

	$: url = $page.url;
	$: currentDocId = url.searchParams.get("docId");
	$: lang = window.location.pathname.includes(`/${$page.data.lang}/`)
		? $page.data.lang
		: "";
	$: record = $store?.[col]?.docs?.[currentDocId] || {};
	$: watchObject(record, "record");

	let responsesSet;

	$: if (
		currentDocId &&
		Object.keys(record).length > 0 &&
		!!record.formName
	) {
		const {
			col,
			settingId,
			formName,
			collection,
			appVersion,
			buildVersion,
		} = record;
		responsesSet = setResponsesDb(
			col,
			settingId,
			formName,
			collection,
			record,
			appVersion,
			buildVersion,
		);
	} else {
		responsesSet = false;
	}

	$: data = {
		...$page.data,
		content: {
			...$page.data?.content,
			...Object.keys($store)
				.filter((k) => $store[k]?.docs)
				.reduce(
					(a, v) => ({
						...a,
						[v]: $store[v].docs,
					}),
					{},
				),
		},
	};

	$: tableFieldsSettingKey = `tf${settingSuffix}`;

	$: tfValue =
		$store?.settings?.docs?.[dbGroup]?.[tableFieldsSettingKey]?.value;

	let tableFields = [];
	let tfUpdateTimeout;

	$: if (tfValue && $store?.[col]?.docs) {
		if (tfUpdateTimeout) clearTimeout(tfUpdateTimeout);
		tfUpdateTimeout = setTimeout(() => {
			tableFields = resolveTableFields(
				tryEval(tfValue),
				$store?.[col]?.docs || {},
				col,
			);
		}, 1000);
	}
	$: providedTableFields = Array.isArray(tableFields) ? tableFields : [];
	$: docs = $store?.[col]?.docs || {};
	$: autoGeneratedFields = buildAutoTableFields(docs, col);
	$: baseFields = {
		...autoGeneratedFields.reduce((a, v) => ({ ...a, [v.heading]: v }), {}),
		...providedTableFields.reduce((a, v) => ({ ...a, [v.heading]: v }), {}),
	};
	$: baseTableFields = Object.entries(baseFields)
		.map(([k, v]) => v)
		.sort((a, b) => (a.heading < b.heading ? -1 : 1));
	$: watchObject(baseTableFields, "baseTableFields");

	$: dashboardSettingKey = `db${settingSuffix}`;
	$: dbValue =
		$store?.settings?.docs?.[dbGroup]?.[dashboardSettingKey]?.value;
	// Dashboard scripts register teardown for anything they start (store
	// subscriptions, intervals, listeners) with vns.onDashboardCleanup(fn).
	// Cleanups run before the script is re-evaluated, when the tab switches
	// to one without a script, and when the dashboard is destroyed.
	let dashboardCleanups = [];
	const runDashboardCleanups = () => {
		for (const cleanup of dashboardCleanups.splice(0)) {
			try {
				cleanup();
			} catch (error) {
				console.error("Dashboard script cleanup failed", error);
			}
		}
	};
	const onDashboardCleanup = (cleanup) => {
		if (typeof cleanup === "function") dashboardCleanups.push(cleanup);
	};
	$: watchObject(updateSearchParams, "updateSearchParams");
	$: watchObject(store.subscribe, "subscribeStore");

	let dashboard = () => {};
	let dbUpdateTimeout;
	$: loadDashboard(dbValue);

	function loadDashboard(value) {
		if (dbUpdateTimeout) clearTimeout(dbUpdateTimeout);
		runDashboardCleanups();
		if (!value) {
			dashboard = () => {};
			return;
		}
		dbUpdateTimeout = setTimeout(() => {
			runDashboardCleanups();
			watchObject(onDashboardCleanup, "onDashboardCleanup");
			dashboard = tryEval(value);
		}, 1000);
	}

	onDestroy(() => {
		if (dbUpdateTimeout) clearTimeout(dbUpdateTimeout);
		runDashboardCleanups();
	});

	let settingUpdateTimeout;
	let tfSettingInitializeTimeout;
	let dbSettingInitializeTimeout;
	let loading;

	const settingUpdate = (event) => {
		if (settingUpdateTimeout) clearTimeout(settingUpdateTimeout);
		settingUpdateTimeout = setTimeout(async () => {
			void formUpdate(event, $store, loading, "settings", dbGroup);
		}, 1000);
	};

	$: if (
		dbGroup &&
		$store?.userRoles?.admin &&
		$store?.settings?.docs &&
		$store.settings.docs[dbGroup]?.[tableFieldsSettingKey] === undefined
	) {
		if (tfSettingInitializeTimeout)
			clearTimeout(tfSettingInitializeTimeout);
		const settingId = dbGroup;
		const settingKey = tableFieldsSettingKey;
		tfSettingInitializeTimeout = scheduleMissingSettingUpdate({
			getCurrentSetting: () =>
				$store.settings.docs?.[settingId]?.[settingKey],
			onMissing: () =>
				recordUpdate("settings", settingId, {
					[settingKey]: createSetting("TextArea"),
				}),
		});
	}

	$: if (
		dbGroup &&
		$store?.userRoles?.admin &&
		$store?.settings?.docs &&
		$store.settings.docs[dbGroup]?.[dashboardSettingKey] === undefined
	) {
		if (dbSettingInitializeTimeout)
			clearTimeout(dbSettingInitializeTimeout);
		const settingId = dbGroup;
		const settingKey = dashboardSettingKey;
		dbSettingInitializeTimeout = scheduleMissingSettingUpdate({
			getCurrentSetting: () =>
				$store.settings.docs?.[settingId]?.[settingKey],
			onMissing: () =>
				recordUpdate("settings", settingId, {
					[settingKey]: createSetting("TextArea"),
				}),
		});
	}
</script>

<section class="mt-6 space-y-4">
	{#if !!currentDocId}
		{#if Array.isArray(baseTableFields)}
			<RecordDetail
				{col}
				docId={currentDocId}
				onClose={clearRecordSelection}
				tableFields={baseTableFields}
				allowFormEdit={!!$store?.userRoles?.admin}
				formDefaultsSettingId={dbGroup}
				allowDelete={!!$store?.userRoles?.admin}
			/>
		{/if}

		{#if responsesSet}
			<div class="shadow-xl collapse collapse-arrow bg-base-300">
				<input type="checkbox" />
				<div class="font-medium collapse-title">
					Original Form Submission
				</div>
				<div class="collapse-content">
					<Form
						lang={$page?.data?.lang}
						{data}
						col={record.col}
						settingId={record.settingId}
						allowReset={false}
						allowHideTags={false}
					/>
				</div>
			</div>
		{/if}
	{:else}
		{#if typeof dashboard === "function"}
			{@html dashboard()}
		{/if}
		<DashboardTable
			{col}
			includeAll={true}
			{action}
			{baseTableFields}
			tableDefaultsSettingId={dbGroup}
			adjustLeftMargin={false}
			allowEdit={false}
			allowTableColumnEdit={!!$store?.userRoles?.admin}
			requiredFields={!!$store?.userRoles?.admin ? [] : undefined}
		/>
	{/if}

	{#if $store?.userRoles?.admin}
		{#if $store?.settings?.docs?.[dbGroup]?.[dashboardSettingKey]}
			<div class="shadow-xl collapse collapse-arrow bg-base-300">
				<input type="checkbox" />
				<div class="font-medium collapse-title">Dashboard Function</div>
				<div class="collapse-content">
					<SettingsFormInput
						col={"settings"}
						settingId={dbGroup}
						fld={{
							key: dashboardSettingKey,
							...$store.settings.docs[dbGroup][
								dashboardSettingKey
							],
						}}
						{lang}
						handleChange={settingUpdate}
					/>
				</div>
			</div>
		{/if}
		{#if $store?.settings?.docs?.[dbGroup]?.[tableFieldsSettingKey]}
			<div class="shadow-xl collapse collapse-arrow bg-base-300">
				<input type="checkbox" />
				<div class="font-medium collapse-title">
					Table Fields Function
				</div>
				<div class="collapse-content">
					<SettingsFormInput
						col={"settings"}
						settingId={dbGroup}
						fld={{
							key: tableFieldsSettingKey,
							...$store.settings.docs[dbGroup][
								tableFieldsSettingKey
							],
						}}
						{lang}
						handleChange={settingUpdate}
					/>
				</div>
			</div>
		{/if}
	{/if}
</section>
